<template>
  <DataTable
    ref="dataTableRef"
    :loading="carriersStore.loading"
    :data="carriersStore.carriers"
    :page-size="pageSize"
    :columns="columns"
    :row-key="rowKey"
    :search="true"
    @update-page="onPaginationChange"
    @update-sorter="onSorterChange"
    @update-filter="onFilterChange"
  >
    <n-button type="primary" @click="openFormModal()"> Add </n-button>
  </DataTable>

  <ConfirmModal ref="confirmModalRef" :on-confirm="onRemoveConfirm">
    <template #header>
      <h5>Confirm Deletion</h5>
    </template>
    <div class="text-center">
      <p class="mb-2">You're about to remove this carrier from your account.</p>
      <h1 class="mb-2">Are you sure?</h1>
    </div>
  </ConfirmModal>

  <CarrierFormModal ref="carrierFormModal" :carrier="carriersStore.selectedCarrier" @on-submit="onSubmit">
    <template v-if="carriersStore.selectedCarrierId"> Update MGA/Carrier </template>
    <template v-else> Add new MGA/Carrier </template>
  </CarrierFormModal>

  <LazyCommissionFormModal
    ref="commissionFormModal"
    :agent-carrier-commission="agentCarrierCommission"
    @submit="onCommissionSubmit"
  />
</template>

<script setup lang="ts">
import type { ComponentExposed } from 'vue-component-type-helpers';
import { Trash, Pencil, CurrencyDollar } from '@vicons/tabler';
import type DataTable from '../../base/components/DataTable.vue';
import type { DataTableColumn } from '../../base/common/interfaces/data-table/data-table-props.interface';
import type { AddCarrierDto, CarrierDto, UpdateCarrierDto } from '../../base/common/dtos/carrier-list.dto';
import { type DataTableCreateRowKey, NList, NListItem, NThing, NIcon } from 'naive-ui';
import DotsMenu from '../../base/components/DotsMenu.vue';
import type {
  DataTableFilterEmitParams,
  DataTablePaginationEmitParams,
  DataTableSorterEmitParams,
} from '../../base/common/interfaces/data-table/data-table-emits.interface';
import type ConfirmModal from '../../base/components/ConfirmModal.vue';
import type CarrierFormModal from './CarrierFormModal.vue';
import type { CarriersFormModalOnSubmitPropsModel } from '../../base/common/models/contact-catalogs/carriers-form-modal-props.model';
import type CommissionFormModal from './CommissionFormModal.vue';
import type { AgentCarrierCommissionDto } from '../../base/common/dtos/agent-carrier-commission.dto';
import type { SaveAgentCarrierCommissionDto } from '../../base/common/dtos/save-agent-carrier-commission.dto';

const dataTableRef = ref<ComponentExposed<typeof DataTable> | undefined>();

const { $toastError, $toastSuccess } = useNuxtApp();
const carriersStore = useCarriersStore();
const pageSize = 5;
const columns: DataTableColumn<CarrierDto>[] = [
  {
    type: 'expand',
    expandable: (rowData: CarrierDto) => !!rowData.address,
    renderExpand: (rowData: CarrierDto) => {
      const address = rowData.address;
      assertIsDefined(address);

      return h(NList, () => [
        h(NListItem, () => h(NThing, { class: 'sr-thing', title: 'Address 1', content: address.address1 })),
        address.address2
          ? h(NListItem, () =>
              h(NThing, {
                class: 'sr-thing',
                title: 'Address 2',
                ...(address.address2 && { content: address.address2 }),
              }),
            ) // additional check needed because of callback and address2 could be changed when callback will be called
          : null,
        h(NListItem, () => h(NThing, { class: 'sr-thing', title: 'State', content: address.state ?? '' })),
        h(NListItem, () => h(NThing, { class: 'sr-thing', title: 'City', content: address.city })),
        h(NListItem, () => h(NThing, { class: 'sr-thing', title: 'Postal code', content: address.postalCode })),
        address.phone
          ? h(NListItem, () =>
              h(NThing, { class: 'sr-thing', title: 'Phone', ...(address.phone && { content: address.phone }) }),
            )
          : null,
      ]);
    },
  },
  {
    title: 'Email',
    key: 'email' as keyof CarrierDto,
    sorter: true,
  },
  {
    title: 'Business name',
    key: 'businessName' as keyof CarrierDto,
    sorter: true,
  },
  {
    key: 'actions',
    render: (rowData: CarrierDto) => {
      return h(DotsMenu, {
        menuItems: [
          {
            label: 'Set commission',
            icon: () => {
              return h(NIcon, null, {
                default: () => h(CurrencyDollar),
              });
            },
            props: {
              onClick: () => {
                openCommissionFormModal(rowData.id);
              },
            },
          },
          {
            label: 'Edit',
            disabled: !rowData.canEdit,
            icon: () => {
              return h(NIcon, null, {
                default: () => h(Pencil),
              });
            },
            props: {
              onClick: () => {
                if (rowData.canEdit) {
                  openFormModal(rowData.id);
                }
              },
            },
          },
          {
            label: 'Delete',
            disabled: !rowData.canDelete,
            icon: () => {
              return h(NIcon, null, {
                default: () => h(Trash),
              });
            },
            props: {
              onClick: () => {
                if (rowData.canDelete) {
                  openConfirmModal(rowData.id);
                }
              },
            },
          },
        ],
      });
    },
  },
];
const rowKey: DataTableCreateRowKey<CarrierDto> = (row: CarrierDto) => row.id;

async function initCarriersTable(): Promise<void> {
  await carriersStore.fetchCarriersList({
    take: pageSize,
  });
}

async function onPaginationChange(options: DataTablePaginationEmitParams): Promise<void> {
  const skip = (options.currentPage - 1) * pageSize;
  await carriersStore.fetchCarriersList({
    skip,
    take: pageSize,
    sortBy: options.sorter?.columnKey as keyof CarrierDto,
    sortOrder: options.sorter?.order,
    search: options.filterValue,
  });
}

async function onSorterChange(options: DataTableSorterEmitParams): Promise<void> {
  await carriersStore.fetchCarriersList({
    take: pageSize,
    sortBy: options.sorter?.columnKey as keyof CarrierDto,
    sortOrder: options.sorter?.order,
    search: options.filterValue,
  });
}

async function onFilterChange(options: DataTableFilterEmitParams): Promise<void> {
  await carriersStore.fetchCarriersList({
    take: pageSize,
    sortBy: options.sorter?.columnKey as keyof CarrierDto,
    sortOrder: options.sorter?.order,
    search: options.filterValue,
  });
}

function selectCarrier(id: number): void {
  carriersStore.selectedCarrierId = id;
}

async function removeCarrier(): Promise<void> {
  await carriersStore.removeSelectedCarrier();
}

async function onRemoveConfirm() {
  await removeCarrier();
  await initCarriersTable();
}

const confirmModalRef = ref<ComponentExposed<typeof ConfirmModal> | null>(null);

const openConfirmModal = (id: number) => {
  selectCarrier(id);
  confirmModalRef.value?.openModal();
};

onMounted(async () => {
  await initCarriersTable();
});

const carrierFormModal = ref<ComponentExposed<typeof CarrierFormModal> | null>(null);

async function openFormModal(id?: number): Promise<void> {
  if (id) {
    selectCarrier(id);

    await carriersStore.fetchSelectedCarrier();
  } else {
    carriersStore.selectedCarrierId = null;
  }

  if (carrierFormModal.value) {
    carrierFormModal.value.openModal();
  }
}

const commissionFormModal = ref<ComponentExposed<typeof CommissionFormModal> | null>(null);
const agentCarrierCommission = ref<AgentCarrierCommissionDto>({ carrierId: 0, carrierName: '', commission: 1 });

async function openCommissionFormModal(id: number): Promise<void> {
  try {
    agentCarrierCommission.value = await carriersStore.fetchAgentCarrierCommission(id);
    console.log('🚀 ~ openCommissionFormModal ~ agentCarrierCommission.value :', agentCarrierCommission.value);

    if (commissionFormModal.value) {
      commissionFormModal.value.openModal();
    }
  } catch {}
}

async function saveNew(model: AddCarrierDto): Promise<void> {
  try {
    await carriersStore.saveNew(model);
  } catch {
    $toastError('Something went wrong...');
  }
}

async function saveExisting(model: UpdateCarrierDto): Promise<void> {
  try {
    await carriersStore.saveExisting(model);
  } catch {
    $toastError('Something went wrong...');
  }
}

async function onSubmit(model: CarriersFormModalOnSubmitPropsModel): Promise<void> {
  const newCarrier: AddCarrierDto = {
    email: model.email,
    businessName: model.businessName,
    commission: model.defaultCommission,
  };

  if (!model.skipAddress) {
    newCarrier.address = model.address;
  }

  if (!model.skipBank && !model.deleteBank) {
    newCarrier.bank = {
      routingNumber: model.bank.routingNumber,
      accountNumber: model.bank.accountNumber,
      accountType: model.bank.accountType,
    };
  }

  if (carriersStore.selectedCarrierId) {
    const updatedCarrier: UpdateCarrierDto = newCarrier;

    updatedCarrier.deleteBank = model.deleteBank;

    await saveExisting(updatedCarrier);
  } else {
    await saveNew(newCarrier);
  }

  dataTableRef.value?.resetTable();
}

const onCommissionSubmit = async (model: SaveAgentCarrierCommissionDto): Promise<void> => {
  try {
    await carriersStore.saveAgentCarrierCommission(model);
    commissionFormModal.value?.closeModal();

    $toastSuccess('Commission is saved');
  } catch {}
};
</script>
